import { Alert } from "@msuite/ui/alert";
import { Button } from "@msuite/ui/button";
import { TextInput, TextInputAddTrigger } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardNumber } from "@msuite/utils/numbers";
import { type InviteUsersSchema, inviteUsersSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/** Props Interface */
interface InviteUsersModalProps extends ModalProps {
	onComplete?: () => Promise<void> | void;
}

export function InviteUsersModal({ isOpen, onClose, onComplete }: InviteUsersModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const methods = useForm({
		defaultValues: {
			emails: [""],
		},
		resolver: resolver(inviteUsersSchema),
	});
	const emails = useWatch({ control: methods.control, name: "emails" });

	/** Query */
	const { data: invitations } = useQuery({
		queryKey: ["invitations"],
		queryFn: async () => {
			if (!organization) return null;
			const response = await client.organizations[":orgId"].invitations.$get({
				param: {
					orgId: guardString(organization!.id),
				},
				query: {
					state: "pending",
				},
			});
			const data = await responseHandler(response);
			return data.invitations;
		},
	});

	/** Functions */
	async function handleOnSubmit(json: InviteUsersSchema) {
		const response = await client.organizations.invite.$post({ json });
		// The response decides whether anything was invited. `create User` is a rule the base set
		// does not carry, so this request is refused for most of the organisation — and a refusal
		// read as a success closes the modal on invitations nobody ever received.
		await responseHandler(response);
		await onComplete?.();
		handleOnClose();
	}

	function handleOnRemove(index: number) {
		const newEmails = [...emails];
		newEmails.splice(index, 1);
		methods.setValue("emails", newEmails);
	}

	function handleOnClose() {
		methods.reset();
		onClose();
	}

	/** Computed */
	const hasPendingInvitations = guardNumber(invitations?.length) > 0;

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={handleOnClose}
				title={t("users.invite_users")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					{hasPendingInvitations && (
						<Alert
							variant="warning"
							spacing="compact"
							title="Offene Einladungen"
							description={`${invitations?.map((invitation) => invitation.email).join(", ")}`}
						/>
					)}
					{emails.map((_, index) => (
						<div
							className="items-end h-stack"
							key={index}
						>
							<TextInput
								id={`email-${index}`}
								label={index === 0 ? t("personnel.fields.email") : undefined}
								control={methods.control}
								name={`emails.${index}`}
								type="email"
								isRequired
								placeholder={t("placeholders.email")}
							/>
							{(emails.length > 1 || index !== 0) && (
								<Button
									icon={<Icon name="X" />}
									size="sm"
									variant="danger-ghost"
									onClick={() => handleOnRemove(index)}
								/>
							)}
						</div>
					))}
					<TextInputAddTrigger
						methods={methods}
						path="emails"
					/>
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
